<template>
  <div
    class="flex w-full flex-col gap-y-4 px-6 py-4"
    data-testid="rx-step-summary"
  >
    <Headline tag="h2" look="h1">
      {{ $t('rxConfigurator.summary.title') }}
    </Headline>

    <div class="grow">
      <ProductConfigurationList
        v-if="productConfig"
        data-testid="rx-summary-product"
        class="box-border pb-4 sm:pb-4"
        :product="productConfig"
        :product-configuration="productConfigItems"
        :description="productColorAndSize"
        :show-quantity="false"
        @click:edit="editStep"
      />

      <SightData
        v-if="hasPrescriptionValues"
        class="mb-4 mt-1"
        :title="$t('rx.prescription.summary.sightDataTitle')"
        :date="prescriptionDate"
        :details="sightDataDetails"
        openable
        start-open
      >
        <template #actions>
          <CircleButton
            :size="8"
            background-color-class="bg-white"
            background-color-hover-class="hover:bg-white"
            :aria-label="$t('rxbasketcardDefault.sightData.edit')"
            :label="$t('rxbasketcardDefault.sightData.edit')"
            :title="$t('rxbasketcardDefault.sightData.edit')"
            no-margin
            border-color="[#D7DDE3]"
            border-color-hover="black"
            data-testid="rx-summary-prescription-edit"
            @click="editPrescription"
          >
            <template #icon>
              <LazyIconFielmannEdit class="size-5" />
            </template>
          </CircleButton>
        </template>
      </SightData>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint sonarjs/no-duplicate-string: 1 */

const props = defineProps({
  hasPrescriptionValues: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
})

const emit = defineEmits(['step:goTo', 'ui:update'])

const { $i18n, $currentShop } = useNuxtApp()
const { state } = await useRxConfiguratorState()

onMounted(() => {
  emit('ui:update', {
    mainAction: 'addToBasket' as RxMainAction,
  })
})

const frameProduct = computed(() => state.value.frame?.product)
const frameVariant = computed(() => state.value.frame?.variant)

const productConfig = computed(
  () =>
    frameProduct.value &&
    frameVariant.value &&
    getProductConfigRxMainItem(frameProduct.value, frameVariant.value),
)

const productConfigItems = computed(() => {
  const configItems: ConfiguratorStateValue[] = Object.entries(state.value)
    .filter(([key]) => !['frame', 'prescriptionValues'].includes(key))
    .map(([_, item]) => item as ConfiguratorStateValue)

  const res = configItems
    .filter(Boolean)
    .map((item) =>
      getProductConfigRxItem(item.product, item.variant, item.variant.price),
    )

  return getProductConfigRxItems(frameVariant.value?.price, res, $i18n)
})

const productColorAndSize =
  frameProduct.value && frameVariant.value
    ? getFrameColorAndSize(frameProduct.value, frameVariant.value)
    : ''

const { prescriptionValues } = useRxPrescription()
const sightDataDetails = mapSightPrescriptionToSightData(
  prescriptionValues.value,
)

const prescriptionDate = computed(() => {
  const today = toLocaleDateTimeString(new Date(), $currentShop)
  return `${$i18n.t('rx.prescription.summary.from')} ${today}`
})

const editStep = (step: any) => {
  const stepIndex = getConfStepIndexByMasterKey(
    step.masterKey,
    props.hasPrescriptionValues,
  )
  emit('step:goTo', stepIndex)
}

const editPrescription = () => {
  const stepIndex = getConfStepIndexByMasterKey('editPrescription', true)
  emit('step:goTo', stepIndex)
}
</script>
